import { useState, useEffect, useLayoutEffect } from "react";
import { useBrand } from "@/lib/brand-context";
import { useContentStore } from "@/store/useContentStore";
import { BoardTag, BOARD_COLOR_PALETTE, DEFAULT_BOARD_TAGS } from "@/types";
import { Trash2, Plus, Bell, HelpCircle } from "lucide-react";
import AppSidebar from "@/components/layout/AppSidebar";
import IngestionSection, { type AnalyzeModelValue } from "./components/IngestionSection";
import RuleManagementTables from "./components/RuleManagementTables";
import BottomActionBar from "./components/BottomActionBar";
import type { OjkIngestResult, ParseOjkPdfResult } from "@seo/shared";
import {
  OJK_LS_BANNED,
  OJK_LS_RESTRICTED,
  OJK_LS_SEED_KEYWORDS,
  readOjkStringArray,
  writeOjkStringArray,
} from "@/lib/ojk-local-storage";

export default function SettingsPage() {
  const { config, setConfig } = useBrand();

  /* OJK master switch — the same `ojkStrict` flag the AI Creator
   *  generation pipeline reads when it runs the post-generation
   *  OJK_TERMS scan. Wiring the Settings toggle directly to the
   *  global store fulfils the migration plan announced when the
   *  per-document toggle was retired from AI Creator: brand-wide
   *  compliance now has exactly one canonical control surface. */
  const ojkStrict = useContentStore((s) => s.ojkStrict);
  const toggleOjk = useContentStore((s) => s.toggleOjk);

  // ── OJK persistent lists ──────────────────────────────────────
  // Strategy:
  //   1. Start with [] (SSR-safe).
  //   2. On mount, read real values from localStorage and set `hydrated = true`.
  //   3. After hydration, any change to the three lists auto-writes to localStorage
  //      immediately — so data is persisted the moment an OJK fetch completes or
  //      the user adds/removes an entry, without requiring a Save click.
  const [bannedEntities,  setBannedEntities]  = useState<string[]>([]);
  const [restrictedTerms, setRestrictedTerms] = useState<string[]>([]);
  const [seedKeywords,    setSeedKeywords]    = useState<string[]>([]);
  const [hydrated,        setHydrated]        = useState(false);

  // Read localStorage before paint so child tables never sync from a transient [].
  useLayoutEffect(() => {
    setBannedEntities(readOjkStringArray(OJK_LS_BANNED));
    setRestrictedTerms(readOjkStringArray(OJK_LS_RESTRICTED));
    setSeedKeywords(readOjkStringArray(OJK_LS_SEED_KEYWORDS));
    setHydrated(true);
  }, []);

  // Auto-persist on every change — but ONLY after hydration so the initial
  // empty [] never overwrites existing data.
  useEffect(() => { if (hydrated) writeOjkStringArray(OJK_LS_BANNED,        bannedEntities);  }, [bannedEntities,  hydrated]);
  useEffect(() => { if (hydrated) writeOjkStringArray(OJK_LS_RESTRICTED,    restrictedTerms); }, [restrictedTerms, hydrated]);
  useEffect(() => { if (hydrated) writeOjkStringArray(OJK_LS_SEED_KEYWORDS, seedKeywords);    }, [seedKeywords,    hydrated]);

  // ── Board tags ────────────────────────────────────────────────
  const [boards, setBoards] = useState<BoardTag[]>(DEFAULT_BOARD_TAGS);
  const [isSaving, setIsSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // API settings — key lives in .env.local (server-side only)
  const [doubaoEp, setDoubaoEp] = useState("");
  const [showKey, setShowKey] = useState(false);

  // Toast — prefix with ❌ to trigger red styling
  const [toast, setToast] = useState<string | null>(null);

  // Hydrate from persisted config on mount
  useEffect(() => {
    if (config.boardTags && config.boardTags.length > 0) {
      setBoards(config.boardTags);
    }
    setDoubaoEp(config.doubaoModelEp || "");
  }, [config.boardTags, config.doubaoModelEp]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  // ── Board CRUD ───────────────────────────────────────────────
  function handleLabelChange(key: string, newLabel: string) {
    setBoards((prev) => prev.map((b) => (b.key === key ? { ...b, label: newLabel } : b)));
    setIsDirty(true);
    setSavedOk(false);
  }

  function handleDeleteBoard(key: string) {
    if (boards.length <= 1) return;
    setBoards((prev) => prev.filter((b) => b.key !== key));
    setIsDirty(true);
    setSavedOk(false);
  }

  function handleAddBoard() {
    const idx = boards.length % BOARD_COLOR_PALETTE.length;
    const color = BOARD_COLOR_PALETTE[idx];
    const newTag: BoardTag = {
      key: `board_${Date.now()}`,
      label: "New Board",
      bg: color.bg,
      text: color.text,
    };
    setBoards((prev) => [...prev, newTag]);
    setIsDirty(true);
    setSavedOk(false);
  }

  async function handleSave() {
    // OJK lists are already auto-persisted via the useEffect watchers above.
    // We still write them here explicitly as a belt-and-suspenders guarantee.
    writeOjkStringArray(OJK_LS_BANNED,        bannedEntities);
    writeOjkStringArray(OJK_LS_RESTRICTED,    restrictedTerms);
    writeOjkStringArray(OJK_LS_SEED_KEYWORDS, seedKeywords);

    setIsSaving(true);
    await new Promise((r) => setTimeout(r, 400));
    setConfig({ ...config, boardTags: boards, doubaoModelEp: doubaoEp });
    setIsSaving(false);
    setIsDirty(false);
    setSavedOk(true);
    showToast("✅ Rules & boards saved — changes are live globally.");
    setTimeout(() => setSavedOk(false), 3000);
  }

  function handleDiscard() {
    if (config.boardTags && config.boardTags.length > 0) setBoards(config.boardTags);
    setDoubaoEp(config.doubaoModelEp || "");
    // Re-read OJK lists from localStorage (the hook auto-wrote the latest saved
    // values there, so this effectively reverts any in-session unsaved changes).
    setBannedEntities(readOjkStringArray(OJK_LS_BANNED));
    setRestrictedTerms(readOjkStringArray(OJK_LS_RESTRICTED));
    setSeedKeywords(readOjkStringArray(OJK_LS_SEED_KEYWORDS));
    setIsDirty(false);
    setSavedOk(false);
  }

  // ── OJK UI-only state (non-persisted) ────────────────────────
  const [ojkUrl, setOjkUrl] = useState("");
  const [analyzeModel, setAnalyzeModel] = useState<AnalyzeModelValue>("claude-3-5-haiku");
  const [ojkLoading, setOjkLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [ojkDone, setOjkDone] = useState(false);
  const [ojkError, setOjkError] = useState("");
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);
  const [ojkSummary, setOjkSummary] = useState("");
  const [ojkRelevant, setOjkRelevant] = useState(false);
  const [newBanned, setNewBanned] = useState("");
  const [newRestricted, setNewRestricted] = useState("");

  async function handleOjkFetch() {
    const trimmed = ojkUrl.trim();
    if (!trimmed) return;

    // IMPORTANT: Do NOT clear bannedEntities / restrictedTerms here.
    // Clearing propagates to RuleManagementTables → sync empties entityRows /
    // termRows → auto-save writes [] to localStorage and wipes all persisted
    // rules (including risk/category metadata) before the fetch completes.
    setOjkLoading(true);
    setOjkDone(false);
    setOjkError("");
    setOjkSummary("");
    setOjkRelevant(false);

    try {
      const res = await fetch("/api/ojk-ingest", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ url: trimmed, model: analyzeModel }),
      });

      let data: OjkIngestResult & { error?: string };
      try {
        data = await res.json();
      } catch {
        throw new Error(`HTTP ${res.status} — server returned a non-JSON response`);
      }

      if (!res.ok) {
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      // Populate state from the rich response
      setBannedEntities(data.bannedEntities  ?? []);
      setRestrictedTerms(data.restrictedTerms ?? []);
      setOjkSummary(data.summary   ?? "");
      setOjkRelevant(data.isRelevant ?? false);
      setOjkDone(true);
      setLastFetchTime(new Date());
      setIsDirty(true); // mark page as having unsaved changes

      // User-facing toast
      if (!data.isRelevant) {
        showToast("ℹ️ URL is not related to Indonesian financial services — no violations found.");
      } else if ((data.bannedEntities?.length ?? 0) === 0 && (data.restrictedTerms?.length ?? 0) === 0) {
        showToast("⚠️ Analysis complete — no violations detected.");
      } else {
        showToast(
          `✅ Found ${data.bannedEntities?.length ?? 0} illegal platforms and ${data.restrictedTerms?.length ?? 0} restricted terms.`
        );
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("❌ [OJK Ingest] fetch failed:", msg);
      setOjkError(msg);
      showToast(`❌ Fetch failed: ${msg}`);
    } finally {
      setOjkLoading(false);
    }
  }

  function mergeUniqueStrings(existing: string[], incoming: string[]): string[] {
    const map = new Map<string, string>();
    for (const item of existing) {
      const trimmed = item.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (!map.has(key)) map.set(key, trimmed);
    }
    for (const item of incoming) {
      const trimmed = item.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (!map.has(key)) map.set(key, trimmed);
    }
    return Array.from(map.values());
  }

  async function handlePdfUpload(file: File) {
    if (!file) return;
    setPdfLoading(true);
    setOjkError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch("/api/parse-ojk-pdf", {
        method: "POST",
        body: formData,
      });

      let data: ParseOjkPdfResult & { error?: string };
      try {
        data = await res.json();
      } catch {
        throw new Error(`HTTP ${res.status} — server returned a non-JSON response`);
      }

      if (!res.ok) {
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      const currentBanned = new Set(bannedEntities.map(v => v.trim().toLowerCase()).filter(Boolean));
      const currentTerms = new Set(restrictedTerms.map(v => v.trim().toLowerCase()).filter(Boolean));
      const toAddBanned = (data.bannedEntities ?? []).filter(v => {
        const key = v.trim().toLowerCase();
        return !!key && !currentBanned.has(key);
      });
      const toAddTerms = (data.restrictedTerms ?? []).filter(v => {
        const key = v.trim().toLowerCase();
        return !!key && !currentTerms.has(key);
      });

      if (toAddBanned.length > 0) {
        setBannedEntities(prev => mergeUniqueStrings(prev, toAddBanned));
      }
      if (toAddTerms.length > 0) {
        setRestrictedTerms(prev => mergeUniqueStrings(prev, toAddTerms));
      }

      const addedCount = toAddBanned.length + toAddTerms.length;
      setIsDirty(true);
      setLastFetchTime(new Date());
      showToast(`成功从 PDF 中提取并添加 ${addedCount} 个合规规则！`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setOjkError(msg);
      showToast(`❌ PDF 解析失败: ${msg}`);
    } finally {
      setPdfLoading(false);
    }
  }

  // ─────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#F7F7FB]">

      {/* LEFT: shared app sidebar */}
      <AppSidebar />

      {/* RIGHT: full-width settings area */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">

        {/* ── Top header ──────────────────────────────────── */}
        <header className="flex-shrink-0 bg-white border-b border-gray-100 px-8 py-4 flex items-center gap-3">
          {/* Title cluster — breadcrumb and master switch share one
           *  flex row so their vertical centres stay locked together,
           *  no matter how the surrounding header reflows. gap-4 is
           *  intentional: tighter than the rest of the header so the
           *  switch reads as "belonging to" the title. */}
          <div className="flex items-center gap-4">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm">
              <span className="text-gray-400 font-medium">Settings</span>
              <span className="text-gray-300">/</span>
              <span className="text-indigo-600 font-semibold">OJK Compliance Engine</span>
            </nav>

            {/* Master switch — interactive control pill in place of the
             *  former static "Engine Status: Active" badge. Bound to
             *  the global `ojkStrict` flag in useContentStore, so a
             *  flip here instantly enables/disables the OJK_TERMS scan
             *  in the AI Creator generation pipeline. */}
            <button
              type="button"
              onClick={toggleOjk}
              role="switch"
              aria-checked={ojkStrict}
              aria-label={`OJK Compliance Engine — ${ojkStrict ? "active" : "inactive"}, click to toggle`}
              className="inline-flex items-center gap-2.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 transition-colors hover:bg-gray-100"
            >
              {/* Switch track + thumb */}
              <span
                className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
                  ojkStrict ? "bg-emerald-500" : "bg-gray-300"
                }`}
                aria-hidden="true"
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${
                    ojkStrict ? "left-4" : "left-0.5"
                  }`}
                />
              </span>
              {/* Dynamic status text */}
              <span
                className={`text-sm font-medium ${
                  ojkStrict ? "text-emerald-600" : "text-gray-500"
                }`}
              >
                {ojkStrict ? "Active" : "Inactive"}
              </span>
            </button>
          </div>

          <div className="flex-1" />

          {/* Right icons */}
          <div className="flex items-center gap-3">
            <button className="text-gray-400 hover:text-gray-600 transition-colors">
              <Bell size={18} />
            </button>
            <button className="text-gray-400 hover:text-gray-600 transition-colors">
              <HelpCircle size={18} />
            </button>
            <div className="flex items-center gap-2 ml-1">
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                AU
              </div>
              <div className="text-right leading-tight">
                <p className="text-xs font-semibold text-gray-700">Admin User</p>
                <p className="text-[10px] text-gray-400">Compliance Lead</p>
              </div>
            </div>
          </div>
        </header>

        {/* ── Scrollable content ──────────────────────────── */}
        <div className="flex-1 overflow-y-auto pb-24">
          <div className="max-w-6xl mx-auto px-8 py-8 space-y-10">

            {/* ① Compliance Ingestion */}
            <IngestionSection
              ojkUrl={ojkUrl}
              onUrlChange={(v) => { setOjkUrl(v); setOjkDone(false); setOjkError(""); }}
              onFetch={handleOjkFetch}
              ojkLoading={ojkLoading}
              ojkDone={ojkDone}
              ojkError={ojkError}
              ojkSummary={ojkSummary}
              ojkRelevant={ojkRelevant}
              bannedCount={bannedEntities.length}
              restrictedCount={restrictedTerms.length}
              lastFetchTime={lastFetchTime}
              analyzeModel={analyzeModel}
              onModelChange={setAnalyzeModel}
              onPdfUpload={handlePdfUpload}
              pdfLoading={pdfLoading}
            />

            {/* ② Rule Management */}
            <RuleManagementTables
              listsHydrated={hydrated}
              bannedEntities={bannedEntities}
              onRemoveBanned={(v) => { setBannedEntities((p) => p.filter((x) => x !== v)); setIsDirty(true); }}
              onAddBanned={(v) => { setBannedEntities((p) => [...p, v]); setIsDirty(true); }}
              newBanned={newBanned}
              onNewBannedChange={setNewBanned}
              restrictedTerms={restrictedTerms}
              onRemoveRestricted={(v) => { setRestrictedTerms((p) => p.filter((x) => x !== v)); setIsDirty(true); }}
              onAddRestricted={(v) => { setRestrictedTerms((p) => [...p, v]); setIsDirty(true); }}
              newRestricted={newRestricted}
              onNewRestrictedChange={setNewRestricted}
            />

            {/* ③ Strategy Board Names — kept as a secondary config card */}
            <section>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900">Strategy Board Names</h2>
                <p className="text-sm text-gray-400 mt-1">
                  {boards.length} boards · changes apply globally to all pages.
                </p>
              </div>

              <div className={`bg-white rounded-2xl border shadow-sm p-6 transition-colors ${isDirty ? "border-amber-300" : "border-gray-100"}`}>
                <div className="space-y-2 mb-4">
                  {boards.map((board) => (
                    <div key={board.key} className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full flex-shrink-0 ${board.bg} ring-1 ring-white shadow-sm`} />
                      <input
                        type="text"
                        value={board.label}
                        onChange={(e) => handleLabelChange(board.key, e.target.value)}
                        className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      />
                      <button
                        onClick={() => handleDeleteBoard(board.key)}
                        disabled={boards.length <= 1}
                        className="text-gray-300 hover:text-red-400 disabled:opacity-20 transition-colors p-1"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleAddBoard}
                  className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
                >
                  <Plus size={15} />
                  Add New Board
                </button>
              </div>
            </section>

            {/* ④ API & Writing Engine — kept as a config card */}
            <section>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900">API &amp; Writing Engine</h2>
                <p className="text-sm text-gray-400 mt-1">Configure the AI engine for article generation.</p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
                {/* Engine indicator */}
                <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
                  <span className="text-sm font-bold text-indigo-700">Doubao (ByteDance)</span>
                  <span className="ml-auto text-xs bg-indigo-200 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">Default</span>
                </div>

                {/* API Key — read from .env.local, cannot be changed via UI */}
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                    Doubao API Key
                  </label>
                  <div className="flex gap-2">
                    <input
                      type={showKey ? "text" : "password"}
                      readOnly
                      value="••••••••••••••••••••••••••••••••"
                      className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-400 cursor-not-allowed"
                    />
                    <button
                      onClick={() => setShowKey(!showKey)}
                      className="px-3 text-xs bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-600 transition-colors"
                    >
                      {showKey ? "Hide" : "Peek"}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Stored in <code className="bg-gray-100 px-1 rounded">.env.local</code> — edit the file directly to rotate the key.</p>
                </div>

                {/* Model endpoint */}
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                    Model Endpoint (DOUBAO_MODEL_EP)
                  </label>
                  <input
                    type="text"
                    value={doubaoEp}
                    onChange={(e) => { setDoubaoEp(e.target.value); setIsDirty(true); }}
                    placeholder="ep-xxxxxxxxxx-xxxxx"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* ── Sticky bottom action bar ─────────────────────── */}
        <BottomActionBar
          isSaving={isSaving}
          isDirty={isDirty}
          savedOk={savedOk}
          onDiscard={handleDiscard}
          onSave={handleSave}
        />
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-20 right-6 text-white text-sm px-5 py-3 rounded-2xl shadow-xl z-50 max-w-sm leading-snug ${
            toast.startsWith("❌")
              ? "bg-red-600"
              : toast.startsWith("⚠️")
              ? "bg-amber-500"
              : "bg-gray-900"
          }`}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
